import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUploadField from "@/components/ImageUploadField";

const EMPTY = { name: "", title: "", role: "", department_id: null, service_ids: [], location: "London", photo_url: "", bio: "", bio_excerpt: "", email: "", phone: "", order: 0 };

export default function AdminLawyers() {
  const { headers } = useAuth();
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [l, d, s] = await Promise.all([
        api.get("/admin/lawyers", { headers }),
        api.get("/admin/departments", { headers }),
        api.get("/admin/services", { headers }),
      ]);
      setItems(l.data); setDepartments(d.data); setServices(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const deptLookup = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d])), [departments]);

  // Departments this lawyer currently heads up (from the DB).
  const currentHeadDeptIds = useMemo(() => {
    if (!editing?.id) return [];
    return departments.filter((d) => d.head_lawyer_id === editing.id).map((d) => d.id);
  }, [departments, editing?.id]);

  // Track the editor's intended Head-of-department selection separately so we
  // can diff & apply changes on save.
  const [headDeptIds, setHeadDeptIds] = useState([]);
  useEffect(() => {
    setHeadDeptIds(currentHeadDeptIds);
    // eslint-disable-next-line
  }, [editing?.id]);

  const toggleHeadDept = (id) => {
    setHeadDeptIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let lawyerId = editing.id;
      if (editing.id) await api.put(`/admin/lawyers/${editing.id}`, editing, { headers });
      else {
        const res = await api.post(`/admin/lawyers`, editing, { headers });
        lawyerId = res.data?.id;
      }

      // Apply head-of-department changes by writing head_lawyer_id on each
      // affected department.
      const toAssign = headDeptIds.filter((id) => !currentHeadDeptIds.includes(id));
      const toUnassign = currentHeadDeptIds.filter((id) => !headDeptIds.includes(id));
      for (const id of toAssign) {
        const dept = deptLookup[id];
        if (dept) await api.put(`/admin/departments/${id}`, { ...dept, head_lawyer_id: lawyerId }, { headers });
      }
      for (const id of toUnassign) {
        const dept = deptLookup[id];
        if (dept) await api.put(`/admin/departments/${id}`, { ...dept, head_lawyer_id: null }, { headers });
      }

      toast.success("Saved.");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this lawyer?")) return;
    try { await api.delete(`/admin/lawyers/${id}`, { headers }); toast.success("Deleted."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleService = (id) => {
    setEditing((cur) => {
      const arr = cur.service_ids || [];
      return { ...cur, service_ids: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] };
    });
  };

  const servicesForCurrentDept = useMemo(() => {
    if (!editing?.department_id) return services;
    return services.filter((s) => s.department_id === editing.department_id);
  }, [services, editing?.department_id]);

  return (
    <div data-testid="admin-lawyers">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="overline text-graphite/55">Lawyers</p>
          <h1 className="mt-3 h-display text-4xl md:text-5xl">Manage people <span className="text-graphite/40 text-xl">· {items.length}</span></h1>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="cec-btn cec-btn-primary" data-testid="admin-add-lawyer">
          <Plus size={16} /> Add lawyer
        </button>
      </div>

      <div className="hairline-top">
        {items.map((l) => (
          <div key={l.id} className="grid grid-cols-12 gap-4 py-4 hairline-bottom items-center" data-testid={`admin-lawyer-${l.id}`}>
            <div className="col-span-1">
              {l.photo_url && <img src={l.photo_url} alt={l.name} className="w-12 h-12 object-cover bw-portrait" />}
            </div>
            <div className="col-span-3">
              <p className="font-semibold">{l.name}</p>
              <p className="text-xs text-graphite/60">{l.title}</p>
            </div>
            <div className="col-span-3 text-sm">
              {deptLookup[l.department_id]?.title || <span className="text-graphite/45">Unassigned</span>}
              {departments.filter((d) => d.head_lawyer_id === l.id).map((d) => (
                <span key={d.id} className="ml-2 inline-block text-[10px] uppercase tracking-[0.14em] bg-[#021D6F] text-warmwhite px-1.5 py-0.5" title={`Head of ${d.title}`}>
                  Head · {d.title}
                </span>
              ))}
            </div>
            <div className="col-span-2 text-sm text-graphite/65">{(l.service_ids || []).length} services</div>
            <div className="col-span-2 text-xs text-graphite/55 truncate">{l.email}</div>
            <div className="col-span-1 flex justify-end gap-1">
              <button className="p-2 hover:text-[#1E3668]" onClick={() => setEditing({ ...l })} aria-label="Edit" data-testid={`admin-edit-${l.id}`}><Pencil size={16} /></button>
              <button className="p-2 hover:text-red-700" onClick={() => del(l.id)} aria-label="Delete" data-testid={`admin-delete-${l.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-graphite/40 flex items-start justify-center px-4 overflow-y-auto py-12" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-warmwhite max-w-4xl w-full p-8 md:p-12 hairline" data-testid="admin-lawyer-form">
            <div className="flex items-start justify-between">
              <div>
                <p className="overline text-graphite/55">{editing.id ? "Edit" : "New"} lawyer</p>
                <h2 className="mt-3 h-display text-3xl">{editing.name || "New lawyer"}</h2>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="p-2"><X size={18} /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mt-8">
              <div><label className="overline text-graphite/55">Name</label><input className="edit-input" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><label className="overline text-graphite/55">Title / role</label><input className="edit-input" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value, role: e.target.value })} /></div>
              <div>
                <label className="overline text-graphite/55">Department</label>
                <select className="edit-input" value={editing.department_id || ""} onChange={(e) => setEditing({ ...editing, department_id: e.target.value || null })} data-testid="admin-lawyer-dept">
                  <option value="">— Unassigned —</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                </select>
              </div>
              <div><label className="overline text-graphite/55">Location</label><input className="edit-input" value={editing.location || ""} onChange={(e) => setEditing({ ...editing, location: e.target.value })} /></div>
              <div className="md:col-span-2">
                <ImageUploadField
                  label="Portrait (square B/W recommended)"
                  value={editing.photo_url}
                  onChange={(url) => setEditing({ ...editing, photo_url: url })}
                  testId="admin-lawyer-photo"
                />
              </div>
              <div><label className="overline text-graphite/55">Email</label><input className="edit-input" type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></div>
              <div><label className="overline text-graphite/55">Phone</label><input className="edit-input" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
              <div><label className="overline text-graphite/55">Display order</label><input type="number" className="edit-input" value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) || 0 })} /></div>
              <div className="md:col-span-2 mt-2">
                <label className="overline text-graphite/55 block mb-2">Bio</label>
                <RichTextEditor
                  value={editing.bio || ""}
                  onChange={(html) => setEditing({ ...editing, bio: html })}
                  placeholder="Career, expertise, notable matters…"
                  minHeight={180}
                  testId="admin-lawyer-bio"
                />
              </div>
            </div>

            <div className="mt-8" data-testid="admin-lawyer-heads">
              <p className="overline text-graphite/55 mb-3">
                Heads of Department <span className="text-graphite/45">— tick any department this lawyer leads</span>
              </p>
              <div className="hairline p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
                {departments.map((d) => {
                  const checked = headDeptIds.includes(d.id);
                  const otherHeadId = d.head_lawyer_id && d.head_lawyer_id !== editing.id ? d.head_lawyer_id : null;
                  const otherHead = otherHeadId ? items.find((x) => x.id === otherHeadId) : null;
                  return (
                    <label key={d.id} className="flex items-start gap-2 py-1.5 text-sm cursor-pointer hover:text-[#1E3668]">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={checked}
                        onChange={() => toggleHeadDept(d.id)}
                        data-testid={`admin-lawyer-head-${d.id}`}
                      />
                      <span className="flex-1">
                        <span className="font-medium">{d.title}</span>
                        {otherHead && !checked && (
                          <span className="block text-[11px] text-graphite/50 leading-tight mt-0.5">
                            Currently led by {otherHead.name} — ticking this will reassign.
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="mt-8" data-testid="admin-lawyer-services">
              <p className="overline text-graphite/55 mb-3">Services ({(editing.service_ids || []).length}) {editing.department_id && <span className="text-graphite/45">— filtered to department</span>}</p>
              <div className="max-h-64 overflow-y-auto hairline p-3 grid grid-cols-1 md:grid-cols-2 gap-x-6">
                {servicesForCurrentDept.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:text-[#1E3668]">
                    <input type="checkbox" checked={(editing.service_ids || []).includes(s.id)} onChange={() => toggleService(s.id)} data-testid={`admin-lawyer-svc-${s.id}`} />
                    <span>{s.title}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-10 flex items-center gap-3">
              <button type="submit" disabled={busy} className="cec-btn cec-btn-primary" data-testid="admin-lawyer-save">{busy ? "Saving…" : "Save"}</button>
              <button type="button" onClick={() => setEditing(null)} className="cec-btn">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
